/**
 * Townhall Program Manifest — Pre-built Show Program Schema
 *
 * Allows hosts to upload a structured JSON file that populates all show content
 * (agenda, polls, questions, presentations, playlist, web URLs) and injects
 * companion briefing context into the AI companion's prompt.
 *
 * Aligned with Room Memory Architecture Layer 0 (pre-session knowledge seed).
 */

import type { PresentationCard, PresentationLayout } from './PresentationCard';
import type { TownhallPollType } from './TownhallAudience';

// ─── Manifest Version ────────────────────────────────────────────────────────

export const PROGRAM_MANIFEST_VERSION = '1.0' as const;

// ─── Manifest Metadata ──────────────────────────────────────────────────────

export interface ProgramManifestMetadata {
    title: string;
    theme?: string;
    topicDomain?: string;
    description?: string;
}

// ─── Agenda ─────────────────────────────────────────────────────────────────

export interface ProgramAgendaTopic {
    label: string;
    searchHint?: string;
    notes?: string;
}

// ─── Polls ──────────────────────────────────────────────────────────────────

export interface ProgramPollOption {
    label: string;
}

export interface ProgramPollDraft {
    question: string;
    type?: TownhallPollType;
    options: ProgramPollOption[];
    showResultsWhileOpen?: boolean;
    allowChangeVote?: boolean;
    maxSelectionsPerUser?: number;
}

// ─── Questions ──────────────────────────────────────────────────────────────

export interface ProgramSeededQuestion {
    text: string;
    displayName?: string;
}

// ─── Presentations ──────────────────────────────────────────────────────────

/**
 * The morning-show producer predates the shared `stat_card` layout name and emits
 * `stat` with its display value in `items[0]`. Keep that wire shape valid at the
 * manifest boundary; the signaling importer normalizes it before cards enter the
 * presentation cache. Existing native PresentationCard layouts pass through.
 */
export type ProgramPresentationLayout = PresentationLayout | 'stat';

export type ProgramPresentationCard = Omit<PresentationCard, 'layout'> & {
    layout: ProgramPresentationLayout;
};

/** Map of topic label → presentation cards for that topic */
export type ProgramPresentationSet = Record<string, ProgramPresentationCard[]>;

// ─── Web Stage ──────────────────────────────────────────────────────────────

export interface ProgramWebStageUrl {
    url: string;
    title?: string;
}

// ─── Playlist ───────────────────────────────────────────────────────────────

export interface ProgramPlaylistItem {
    url: string;
    title?: string;
}

// ─── Companion Briefing ─────────────────────────────────────────────────────

/** Emotion vocabulary accepted from Morning Rundown program files. */
export const PROGRAM_STORY_EMOTIONS = [
    'neutral',
    'excited',
    'frustrated',
    'amused',
    'skeptical',
    'surprised',
    'contemplative',
    'heated',
    'agreeing',
] as const;

export type ProgramStoryEmotion = typeof PROGRAM_STORY_EMOTIONS[number];

/**
 * A public YouTube clip the producer attached to a story as B-roll.
 *
 * The Morning Rundown producer writes these (`scripts/broll.py`): every clip is
 * oEmbed-verified as public and embeddable at production time, `channel` is the
 * uploader, and `query` is the search that found it. Consumers play them on the
 * stage through the shared stage-media queue; the companion never reads them.
 */
export interface ProgramStoryBroll {
    /** Always a `youtube.com/watch` (or `youtu.be`) link. */
    url: string;
    title?: string;
    channel?: string;
    query?: string;
}

// ─── Program stage media ───────────────────────────────────────────────────

export const PROGRAM_STAGE_MEDIA_KINDS = ['video', 'web'] as const;
export type ProgramStageMediaKind = typeof PROGRAM_STAGE_MEDIA_KINDS[number];

/**
 * One candidate for the shared program-media surface. `web` covers ordinary
 * pages, articles, and PDFs; YouTube clips use `video` so they travel through
 * the synchronized stage-media player.
 */
export interface ProgramStageMediaCue {
    kind: ProgramStageMediaKind;
    url: string;
    title?: string;
    channel?: string;
    /** At most one cue per segment/story should be selected for automatic display. */
    autoLoad?: boolean;
}

/** Per-story editorial and delivery guidance supplied by a show producer. */
export interface ProgramStoryGuidance {
    headline: string;
    source?: string;
    emotion: ProgramStoryEmotion;
    take?: string;
    /** B-roll clips for this story. Empty or absent when no acceptable public clip exists. */
    broll?: ProgramStoryBroll[];
    /** Unified video/web/PDF choices for this story. Optional for version 1.0 compatibility. */
    stageMedia?: ProgramStageMediaCue[];
}

export interface ProgramTopicGuidance {
    topicLabel: string;
    talkingPoints?: string[];
    stance?: string;
    tone?: string;
    /** The segment's editorial angle. Optional for version 1.0 compatibility. */
    take?: string;
    keyFacts?: string[];
    /** Story-specific source, angle, and delivery cues. Optional for older manifests. */
    stories?: ProgramStoryGuidance[];
    /** Media shown when the segment starts, before a story-specific cue takes over. */
    stageMedia?: ProgramStageMediaCue[];
}

export interface ProgramFactSheetEntry {
    claim: string;
    source?: string;
    verified?: boolean;
}

export interface ProgramCompanionBriefing {
    showNarrative: string;
    coldOpen?: string;
    perTopicGuidance?: ProgramTopicGuidance[];
    factSheet?: ProgramFactSheetEntry[];
    hostInstructions?: string;
}

// ─── Root Manifest ──────────────────────────────────────────────────────────

export interface TownhallProgramManifest {
    version: typeof PROGRAM_MANIFEST_VERSION;
    metadata: ProgramManifestMetadata;
    agenda: ProgramAgendaTopic[];
    polls?: ProgramPollDraft[];
    seededQuestions?: ProgramSeededQuestion[];
    presentations?: ProgramPresentationSet;
    webStageUrls?: ProgramWebStageUrl[];
    playlist?: ProgramPlaylistItem[];
    companionBriefing?: ProgramCompanionBriefing;
}

// ─── Upload Result ──────────────────────────────────────────────────────────

export interface ProgramManifestSummary {
    title: string;
    topicCount: number;
    pollCount: number;
    questionCount: number;
    presentationTopicCount: number;
    presentationCardCount: number;
    /** Legacy count retained for previously stored version 1.0 summaries. */
    webStageUrlCount: number;
    /** Unique segment/story media sources, including promoted legacy links and B-roll. */
    stageMediaCount?: number;
    playlistCount: number;
    hasBriefing: boolean;
    briefingTopicGuidanceCount: number;
    briefingFactCount: number;
    /** Optional so status payloads stored before this field remain readable. */
    briefingStoryCount?: number;
}

export interface ProgramManifestUploadResult {
    success: boolean;
    summary: ProgramManifestSummary;
    errors: string[];
    /** Non-fatal import adjustments made to keep an older producer compatible. */
    warnings?: string[];
}

// ─── Status ─────────────────────────────────────────────────────────────────

export interface ProgramManifestStatus {
    loaded: boolean;
    metadata: ProgramManifestMetadata | null;
    loadedAt: number | null;
    loadedBy: string | null;
    summary: ProgramManifestSummary | null;
    programStarted?: boolean;
    programStartedAt?: number | null;
}

// ─── Validation Limits ──────────────────────────────────────────────────────

export const PROGRAM_MANIFEST_LIMITS = {
    maxAgendaTopics: 20,
    maxPolls: 10,
    maxPollOptions: 10,
    maxSeededQuestions: 25,
    maxPresentationTopics: 20,
    maxCardsPerTopic: 10,
    maxWebStageUrls: 5,
    maxPlaylistItems: 20,
    maxBriefingNarrativeChars: 2000,
    maxTopicGuidanceChars: 500,
    maxStoriesPerTopic: 50,
    maxStoryHeadlineChars: 500,
    maxStorySourceChars: 300,
    maxStoryTakeChars: 1000,
    maxFactSheetEntries: 30,
    maxColdOpenChars: 500,
    maxBrollPerStory: 3,
    maxBrollTitleChars: 300,
    maxBrollChannelChars: 200,
    maxBrollQueryChars: 300,
} as const;

// ─── B-roll helpers ─────────────────────────────────────────────────────────

/**
 * Extract the 11-character video id from any common YouTube URL form
 * (`watch?v=`, `youtu.be/`, `/embed/`, `/shorts/`). Returns null for anything
 * else, including non-YouTube hosts — the same forms the stage-media queue accepts.
 */
export function extractYouTubeVideoId(url: string): string | null {
    if (typeof url !== 'string' || !url.trim()) return null;
    const trimmed = url.trim();
    let parsed: URL;
    try {
        parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    } catch {
        return null;
    }
    if (!/(^|\.)youtube\.com$|(^|\.)youtu\.be$|(^|\.)youtube-nocookie\.com$/i.test(parsed.hostname)) {
        return null;
    }
    const candidate =
        parsed.searchParams.get('v')
        ?? /youtu\.be\/([^/?&#]+)/i.exec(trimmed)?.[1]
        ?? /\/embed\/([^/?&#]+)/i.exec(parsed.pathname)?.[1]
        ?? /\/shorts\/([^/?&#]+)/i.exec(parsed.pathname)?.[1]
        ?? null;
    return candidate && /^[a-zA-Z0-9_-]{11}$/.test(candidate) ? candidate : null;
}

/** One playable B-roll clip, flattened with the story and segment it belongs to. */
export interface ProgramBrollClip {
    topicLabel: string;
    headline: string;
    emotion: ProgramStoryEmotion;
    url: string;
    videoId: string;
    title: string;
    channel?: string;
}

export type ProgramStageMediaScope = 'segment' | 'story';

/** A resolved cue used by the host panel and automatic stage controller. */
export interface ProgramStageMediaItem {
    id: string;
    scopeKey: string;
    scope: ProgramStageMediaScope;
    topicLabel: string;
    storyIndex?: number;
    storyHeadline?: string;
    kind: ProgramStageMediaKind;
    url: string;
    title: string;
    channel?: string;
    autoLoad: boolean;
    /** Identifies compatibility items synthesized from the older B-roll field. */
    legacyBroll?: boolean;
}

/**
 * Flatten every story's B-roll in agenda order, dropping clips whose URL is not
 * a recognizable YouTube video. This is what the host's Program panel lists and
 * queues onto the stage.
 */
export function listProgramBroll(briefing: ProgramCompanionBriefing | null | undefined): ProgramBrollClip[] {
    const clips: ProgramBrollClip[] = [];
    for (const guidance of briefing?.perTopicGuidance ?? []) {
        for (const story of guidance.stories ?? []) {
            for (const clip of story.broll ?? []) {
                const videoId = extractYouTubeVideoId(clip.url);
                if (!videoId) continue;
                clips.push({
                    topicLabel: guidance.topicLabel,
                    headline: story.headline,
                    emotion: story.emotion,
                    url: clip.url,
                    videoId,
                    title: clip.title?.trim() || story.headline,
                    channel: clip.channel,
                });
            }
        }
    }
    return clips;
}
